/*
 * Created on Wed Oct 11 2023 4:56:55 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023 
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */

import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaFileInvoice, FaEye, FaDownload } from 'react-icons/fa';
import { resolveReceiptStatus } from '../utils/receiptStatus';

const StatusBadge = ({ invoice, order }) => {
    const meta = resolveReceiptStatus(invoice, invoice?.order || order);
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${meta.bg} ${meta.text} ${meta.border}`}>
            {meta.label}
        </span>
    );
};

/**
 * InvoiceSelectionModal
 *
 * Props
 * ─────
 * show                  boolean          — controls visibility
 * invoices              Invoice[]        — full array returned by the API
 * order                 Order            — the parent order (for header info)
 * onClose               () => void
 * onView                (invoice) => void
 * onDownload            (invoice) => void
 * getDomainTextColor    () => string     — brand colour helper
 */
const InvoiceSelectionModal = ({
    show,
    invoices = [],
    order,
    onClose,
    onView,
    onDownload,
    getDomainTextColor,
    hideDownload = false,
}) => {
    const brandColor = getDomainTextColor?.() || 'var(--flex-primary, #3b82f6)';

    return (
        <AnimatePresence>
            {show && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        key="backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
                        onClick={onClose}
                    />

                    {/* Panel */}
                    <motion.div
                        key="panel"
                        initial={{ opacity: 0, scale: 0.95, y: 16 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 16 }}
                        transition={{ type: 'spring', stiffness: 340, damping: 28 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                    >
                        <div
                            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg pointer-events-auto overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                                <div className="flex items-center gap-3">
                                    <div
                                        className="w-9 h-9 rounded-lg flex items-center justify-center"
                                        style={{ backgroundColor: `${brandColor}18` }}
                                    >
                                        <FaFileInvoice style={{ color: brandColor }} className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <h2 className="text-base font-semibold text-slate-900 leading-tight">
                                            Select Invoice
                                        </h2>
                                        {order && (
                                            <p className="text-xs text-slate-500 mt-0.5">
                                                Order&nbsp;
                                                <span className="font-medium text-slate-700">
                                                    #{order.number || order.orderNumber || order.primaryId}
                                                </span>
                                                &nbsp;·&nbsp;{invoices.length} invoice{invoices.length !== 1 ? 's' : ''} found
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                                    aria-label="Close"
                                >
                                    <FaTimes className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Invoice list */}
                            <div className="px-6 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
                                {invoices.map((invoice, idx) => {
                                    const invNumber = invoice.number || invoice.invoiceNumber || `INV-${idx + 1}`;
                                    const invDate   = invoice.createdDate
                                        ? new Date(invoice.createdDate).toLocaleDateString(undefined, {
                                              day: '2-digit', month: 'short', year: 'numeric',
                                          })
                                        : null;

                                    return (
                                        <motion.div
                                            key={invoice.primaryId || idx}
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: idx * 0.06 }}
                                            className="flex items-center gap-4 p-4 rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all bg-slate-50/50"
                                        >
                                            {/* Left — invoice info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-sm font-semibold text-slate-800 truncate">
                                                        #{invNumber}
                                                    </span>
                                                    <StatusBadge invoice={invoice} order={order} />
                                                </div>
                                                {invDate && (
                                                    <p className="text-xs text-slate-500 mt-1">{invDate}</p>
                                                )}
                                            </div>

                                            {/* Right — actions */}
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                <button
                                                    onClick={() => onView(invoice)}
                                                    title="View receipt"
                                                    className="inline-flex min-h-[44px] lg:min-h-0 items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-400 transition-colors"
                                                >
                                                    <FaEye className="w-3 h-3" />
                                                    View
                                                </button>
                                                {!hideDownload && (
                                                    <button
                                                        onClick={() => onDownload(invoice)}
                                                        title="Download invoice"
                                                        className="inline-flex min-h-[44px] lg:min-h-0 items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-opacity hover:opacity-90"
                                                        style={{ backgroundColor: brandColor }}
                                                    >
                                                        <FaDownload className="w-3 h-3" />
                                                        Download
                                                    </button>
                                                )}
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>

                            {/* Footer */}
                            <div className="px-6 py-4 border-t border-slate-100 flex justify-end">
                                <button
                                    onClick={onClose}
                                    className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default InvoiceSelectionModal;
